/**
 * Apply anisoconia (head scaling) effect to image data
 * Creates a chin-shaped boundary for natural head scaling
 * @param imageData - ImageData to modify
 * @param headScale - Scale factor for the head (1 = no change)
 * @returns New scaled ImageData
 */
export function applyAnisoconiaToImageData(
	imageData: ImageData,
	headScale: number,
): ImageData {
	if (headScale === 1) return imageData;

	const width = imageData.width;
	const height = imageData.height;
	const data = imageData.data;
	const output = new ImageData(width, height);
	const outData = output.data;

	// Chin-shaped boundary parameters
	const headCenterX = width / 2.1;
	const headCenterY = height * 0.25;
	const headRadiusX = width * 0.4;
	const headRadiusY = height * 0.55;

	// Function to check if point is inside chin-shaped boundary
	function isInsideChinBoundary(x: number, y: number): boolean {
		const normX = (x - headCenterX) / headRadiusX;
		const normY = (y - headCenterY) / headRadiusY;

		// Basic ellipse check
		if (normX * normX + normY * normY > 1) return false;

		// Chin-specific adjustments
		// For larger heads, extend the boundary down to include more of the chin/throat area
		if (headScale > 1) {
			// Reduce the boundary below the chin for growing heads
			const chinReduction = (headScale - 1) * headRadiusY * 0.3; // Positive value reduces the area
			const reducedY = headCenterY + headRadiusY - chinReduction; // Subtract to reduce area

			// Use smooth falloff based on distance from center
			const distanceFromCenter = Math.sqrt(normX * normX + normY * normY);
			const maxDistance = 1.0; // Standard ellipse boundary

			// Create smooth falloff for the reduced area
			if (y > reducedY) {
				const extraDistance = (y - reducedY) / (headRadiusY * 0.3); // Normalize extra distance
				const adjustedMaxDistance = maxDistance - extraDistance * 0.8; // Reduce boundary as we go down
				if (distanceFromCenter > adjustedMaxDistance) return false;
			}

			// Asymmetric narrowing for more natural chin shape
			// Left side (negative x) gets more narrowing than right side
			const chinNarrowing = Math.max(0, (y - headCenterY) / headRadiusY) * 0.3;
			let adjustedRadiusX = headRadiusX * (1 - chinNarrowing);

			// Additional left-side adjustment for better chin contour
			if (x < headCenterX) {
				// Left side gets extra narrowing
				const leftSideNarrowing =
					Math.max(0, (y - headCenterY) / headRadiusY) * 0.27;
				adjustedRadiusX *= 1 - leftSideNarrowing;
			}

			const adjustedNormX = (x - headCenterX) / adjustedRadiusX;
			if (adjustedNormX * adjustedNormX + normY * normY > 1) return false;
		}

		return true;
	}

	const shrink = 1 - headScale;
	const moveDown = shrink * headRadiusY;

	// Add vertical shift to maintain throat connection
	// Use different multipliers for shrinking vs growing
	let verticalShift: number;
	if (headScale < 1) {
		// Head is shrinking: move up with smaller multiplier
		verticalShift = shrink * headRadiusY * 0.3;
	} else {
		// Head is growing: move down with larger multiplier
		verticalShift = shrink * headRadiusY * 0.35; // Larger value for growing heads
	}

	// For each pixel, check if inside the chin-shaped boundary
	for (let y = 0; y < height; y++) {
		for (let x = 0; x < width; x++) {
			if (isInsideChinBoundary(x, y)) {
				// Inside chin boundary: map to scaled area with vertical compensation
				const scaledX = headCenterX + (x - headCenterX) / headScale;
				const scaledY =
					headCenterY +
					(y - headCenterY - moveDown + verticalShift) / headScale;
				const srcX = Math.max(0, Math.min(width - 1, Math.round(scaledX)));
				const srcY = Math.max(0, Math.min(height - 1, Math.round(scaledY)));
				const srcIdx = (srcY * width + srcX) * 4;
				const dstIdx = (y * width + x) * 4;
				outData[dstIdx] = data[srcIdx] ?? 0;
				outData[dstIdx + 1] = data[srcIdx + 1] ?? 0;
				outData[dstIdx + 2] = data[srcIdx + 2] ?? 0;
				outData[dstIdx + 3] = data[srcIdx + 3] ?? 255;
			} else {
				// Outside chin boundary: copy as is
				const idx = (y * width + x) * 4;
				outData[idx] = data[idx] ?? 0;
				outData[idx + 1] = data[idx + 1] ?? 0;
				outData[idx + 2] = data[idx + 2] ?? 0;
				outData[idx + 3] = data[idx + 3] ?? 255;
			}
		}
	}
	return output;
}
